import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { formatMoney } from '@dorixona/utils'
import { BackButton } from '../../ui/BackButton'
import { ShareButton } from './ShareButton'
import { FavoriteButton } from '../favorites'
import { env } from '../../config/env'
import { Icon } from '../../ui/Icon'
import { Failed, Loading } from '../../ui/Load'
import { Photo } from '../../ui/Photo'
import { ProductCard } from '../../ui/ProductCard'
import { QtyStepper } from '../../ui/QtyStepper'
import { ApiGapRow } from '../../ui/Gaps'
import { categoryTint, priceLabel, productSubtitle } from '../../ui/product'
import { useCart } from '../cart/CartProvider'
import { useAvailability, useProduct, useSimilar } from './api'
import { MISSING_SPECS, specs } from './product'
import { canAddToCart, cashbackFor, hasInstruction } from './view'
import { StockPill } from './StockPill'
import { usePoints } from '../../ui/points'

export function ProductScreen() {
  const { productId } = useParams()
  const navigate = useNavigate()
  const cart = useCart()
  const { t, i18n } = useTranslation(['catalog', 'common'])
  const { formatPoints } = usePoints()
  const [shot, setShot] = useState(0)

  const id = Number(productId)
  const product = useProduct(id, i18n.language)
  const availability = useAvailability(id)
  const similar = useSimilar(id, i18n.language)

  if (product.isPending) {
    return (
      <section className="screen">
        <Loading what={t('common:state.loading')} shape="page" />
      </section>
    )
  }

  if (product.isError) {
    return (
      <section className="screen">
        <div className="card empty">
          <Icon name="i-search" size={54} />
          <div className="t">{t('catalog:product.notFound')}</div>
          <div style={{ marginTop: 18 }}>
            <Button
              onPress={() => {
                void navigate('/catalog')
              }}
            >
              {t('common:actions.toCatalog')}
            </Button>
          </div>
        </div>
      </section>
    )
  }

  const item = product.data
  const alike = similar.data ?? []
  const quantity = cart.quantityOf(item.id)
  // Кэшбэк — за всё, что уже в корзине, а не за одну штуку.
  const cashback = cashbackFor(item, Math.max(quantity, 1))
  const photos = item.photos
  const current = photos[shot]
  // Ни одна аптека не даст и штуки (`max_qty = 0`) — «В корзину» неактивна.
  const canAdd = canAddToCart(item) && item.maxQty > 0

  return (
    <section className="screen prod-wrap">
      <div className="mhead pp-head">
        <BackButton to="/catalog" />
        <div className="crumbs">
          <button
            type="button"
            onClick={() => {
              void navigate('/catalog')
            }}
          >
            {t('catalog:title')}
          </button>
          <span>/</span>
          {item.categoryName ? (
            <>
              <button
                type="button"
                onClick={() => {
                  void navigate(`/catalog?cat=${item.categoryId}`)
                }}
              >
                {item.categoryName}
              </button>
            </>
          ) : null}
        </div>
        {/* Действия с товаром — одной группой и одного размера. */}
        <div className="pp-head-acts">
          {env.features.favorites ? <FavoriteButton productId={item.id} place="head" /> : null}
          <ShareButton url={window.location.href} title={item.name} />
        </div>
      </div>

      <div className="pp-stack">
        <div className="pp-card pp-top">
          <div className="pp-media">
            <div className="gmain" style={{ background: categoryTint(item.categoryId) }}>
              <Photo
                src={current}
                alt={current ? t('catalog:product.photoOf', { name: item.name, n: shot + 1 }) : ''}
                iconSize={150}
              />
              {photos.length > 1 ? (
                <span className="gcount num">
                  {shot + 1} / {photos.length}
                </span>
              ) : null}
            </div>
            {photos.length > 1 ? (
              <div className="gthumbs">
                {photos.map((photo, index) => (
                  <Press
                    key={photo}
                    squish="chip"
                    className={index === shot ? 'gth on' : 'gth'}
                    aria-label={t('catalog:product.photo', { n: index + 1 })}
                    onPress={() => {
                      setShot(index)
                    }}
                  >
                    <Photo src={photo} alt="" iconSize={28} />
                  </Press>
                ))}
              </div>
            ) : null}
          </div>

          <div className="pp-info">
            <h1 className="pp-title">{item.name}</h1>
            <div className="pp-sub">{productSubtitle(item)}</div>
            <div className="pp-badges">
              {/* Кэшбэка нет — нет и «Кэшбэк 0%»: обещание ни о чём. */}
              {item.cb > 0 ? (
                <span className="pill cb num">
                  {t('catalog:product.cashback', { percent: item.cb })}
                </span>
              ) : null}
              {item.rx ? <span className="pill rx">{t('catalog:product.rx')}</span> : null}
              {item.popular ? (
                <span
                  className="pill"
                  style={{ background: 'var(--gold-soft)', color: 'var(--gold)' }}
                >
                  {t('catalog:product.hit')}
                </span>
              ) : null}
              {item.categoryName ? (
                <span
                  className="pill"
                  style={{ background: 'var(--surface2)', color: 'var(--text2)' }}
                >
                  {item.categoryName}
                </span>
              ) : null}
            </div>

            {item.rx ? (
              <div className="pp-block">
                <div className="rx-note" style={{ margin: 0 }}>
                  <Icon name="i-info" size={18} />
                  {t('catalog:product.rxNote')}
                </div>
              </div>
            ) : !item.inStock ? (
              <div className="pp-block">
                <div className="rx-note" style={{ margin: 0 }}>
                  <Icon name="i-info" size={18} />
                  {t('catalog:product.outNote')}
                </div>
              </div>
            ) : (
              <div className="pp-block">
                <div className="pp-priceline">
                  <span className="pp-price num">
                    {quantity
                      ? formatMoney(item.price * quantity)
                      : priceLabel(item, formatMoney, t('catalog:priceUnknown'))}
                  </span>
                </div>
                {quantity ? (
                  <div className="pp-forwhat">
                    {quantity} × {formatMoney(item.price)}
                  </div>
                ) : null}
                <div className="pp-actions">
                  {quantity ? (
                    <QtyStepper
                      className="qty-big"
                      productId={item.id}
                      quantity={quantity}
                      max={item.maxQty}
                      onChange={(delta) => {
                        cart.changeQuantity(item.id, delta, item.maxQty)
                      }}
                    />
                  ) : null}
                  {/* Товар уже в корзине — рядом со счётчиком путь к ней. */}
                  {quantity ? (
                    <Button
                      onPress={() => {
                        void navigate('/cart')
                      }}
                    >
                      {t('catalog:goToCart')}
                    </Button>
                  ) : (
                    <Button
                      disabled={!canAdd}
                      haptic="key"
                      onPress={() => {
                        cart.changeQuantity(item.id, 1, item.maxQty)
                      }}
                    >
                      {t('catalog:addToCart')}
                    </Button>
                  )}
                </div>
                {cashback > 0 ? (
                  <div className="pp-cb">
                    <Icon name="i-gift" size={17} />
                    {t('catalog:product.cashbackFor', { amount: formatPoints(cashback) })}
                  </div>
                ) : null}
              </div>
            )}

            <div className="pp-facts">
              <div className="pp-fact">
                <Icon name="i-store" size={17} />
                <span>
                  <b>{t('catalog:product.pickup')}</b> {t('catalog:product.pickupRest')}
                </span>
              </div>
              <div className="pp-fact">
                <Icon name="i-info" size={17} />
                <span>{t('catalog:product.noReturn')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className={hasInstruction(item) ? 'pp-cols' : 'pp-cols pp-cols-one'}>
          <div className="pp-card">
            <h2>{t('catalog:product.about')}</h2>
            {item.texts.desc ? (
              <p className="pp-desc">{item.texts.desc}</p>
            ) : (
              <p className="pp-desc">{t('catalog:product.descSoon')}</p>
            )}
            <div className="pp-label">{t('catalog:product.specs')}</div>
            <div className="spec">
              {specs(item, t).map((spec) => (
                <div key={spec.label} className="spec-row">
                  <span>{spec.label}</span>
                  <b>{spec.value}</b>
                </div>
              ))}
            </div>
            <ApiGapRow what={MISSING_SPECS} where="GET /catalog/products/{id}" />
          </div>

          {hasInstruction(item) ? (
            <div className="pp-card">
              <h2>{t('catalog:product.instruction')}</h2>
              {item.texts.indications ? (
                <details className="acc">
                  <summary>{t('catalog:product.indications')}</summary>
                  <p>{item.texts.indications}</p>
                </details>
              ) : null}
              {item.texts.contra ? (
                <details className="acc">
                  <summary>{t('catalog:product.contra')}</summary>
                  <p>{item.texts.contra}</p>
                </details>
              ) : null}
              {item.texts.dosage ? (
                <details className="acc">
                  <summary>{t('catalog:product.dosage')}</summary>
                  <p>{item.texts.dosage}</p>
                </details>
              ) : null}
              {item.texts.storage ? (
                <details className="acc">
                  <summary>{t('catalog:product.storage')}</summary>
                  <p>{item.texts.storage}</p>
                </details>
              ) : null}
              <div className="pp-disclaim">{t('catalog:product.disclaimer')}</div>
            </div>
          ) : null}
        </div>

        <div className="pp-card">
          <h2>
            {t('catalog:product.availability')}
            {/* Только аптеки из списка ниже — те, где товар есть. */}
            {availability.data?.length ? (
              <Press
                squish="chip"
                className="sect-chip"
                onPress={() => {
                  void navigate(`/catalog/${id}/stores`)
                }}
              >
                <Icon name="i-pin" size={15} />
                {t('catalog:product.allOnMap')}
              </Press>
            ) : null}
          </h2>
          {availability.isPending ? (
            <Loading what={t('common:state.loading')} shape="lines" />
          ) : null}
          {availability.isError ? (
            <Failed
              error={availability.error}
              onRetry={() => {
                void availability.refetch()
              }}
            />
          ) : null}
          {availability.data?.length === 0 ? (
            <div className="pp-desc">{t('catalog:product.noneInStores')}</div>
          ) : null}
          <div className="pp-stores">
            {(availability.data ?? []).map((row) => (
              <div key={row.storeId} className="availrow">
                <Icon name="i-pin" size={17} />
                <div className="ad">
                  <div>{row.storeName}</div>
                  {row.storeName.includes(row.district) ? null : (
                    <div className="a2">{row.district}</div>
                  )}
                </div>
                <div className="ap num">
                  {formatMoney(row.price)}
                  <StockPill qty={row.qty} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {alike.length > 0 ? (
          <div className="pp-card">
            <h2>{t('catalog:product.similar')}</h2>
            <div className="pp-sim">
              {alike.map((other) => (
                <ProductCard
                  key={other.id}
                  product={other}
                  compact
                  onOpen={(nextId) => {
                    void navigate(`/catalog/${nextId}`)
                    setShot(0)
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}
